import {
  ROOT_SESSION_STATUS_LABELS,
  type RootSessionStatus,
  type RootSessionStripThreadInput,
} from "./rootSessionStrip";
import { retiredWorldSessionKey } from "./neucleos/retiredWorldSessions";
import { threadBelongsToWorld, type WorldThreadScope } from "./neucleos/worldSelection";

// The agent strip (agent-strip phase 1, 6 October 2026): one ring per live
// session beside Search & commands, so what every session is doing is ambient
// and the Inbox is summoned. The projection is pure: statuses arrive already
// resolved by useRootSessionThreadInputs (resolveSidebarThreadStatus), so the
// strip can never disagree with the session tabs, World doors or rail about
// what a session is doing. Nothing here is read off the screen.

/** The three states a person needs; detail stays on the card. */
export type AgentStripState = "needs" | "working" | "done";

export function agentStripState(status: RootSessionStatus): AgentStripState {
  switch (status) {
    case "approval":
    case "input":
    case "failed":
    case "limited":
      return "needs";
    case "working":
    case "monitoring":
      return "working";
    case "done":
      return "done";
  }
}

export interface AgentStripDot {
  readonly key: string;
  readonly environmentId: string;
  readonly threadId: string;
  /** The World session the thread lives in; null for Local work. */
  readonly worldKey: string | null;
  readonly sessionId: string | null;
  readonly label: string;
  readonly status: RootSessionStatus;
  readonly state: AgentStripState;
  /** "Title, Approval needed": the dot's accessible name and tooltip. */
  readonly description: string;
  /** When its latest turn completed or the session failed; null when unknown. */
  readonly finishedAt: string | null;
}

export interface AgentStripProjection {
  /** Every live session, in strip order. */
  readonly sessions: ReadonlyArray<AgentStripDot>;
  /**
   * Finished work the person has not seen yet (a new result, or a failure not
   * yet seen), newest first: the Inbox's Finished cards (agent-strip phase 2).
   */
  readonly finished: ReadonlyArray<AgentStripDot>;
  readonly dots: ReadonlyArray<AgentStripDot>;
  /** Live sessions beyond the visible dots, shown as "+N". */
  readonly overflow: number;
  readonly needsCount: number;
  readonly doneCount: number;
}

/**
 * W-fa2578b8 (Can 36): a conversation kept in the World waiting on the person ("approval") waits on a card, which the strip
 * shows and counts under Decisions; its ring stays (what that conversation is doing), but it is not counted or listed a
 * second time. A Mac thread's own approval has no such card and stays.
 */
export const WORLD_CONVERSATION_DOT_ENVIRONMENT = "world-conversation";
export const decidedElsewhere = (dot: Pick<AgentStripDot, "environmentId" | "status">): boolean =>
  dot.environmentId === WORLD_CONVERSATION_DOT_ENVIRONMENT && dot.status === "approval";

/** A titlebar seat, not a list: the rest is one "+N" away in the Inbox. */
export const AGENT_STRIP_MAX_DOTS = 7;

const STATE_ORDER: Readonly<Record<AgentStripState, number>> = { needs: 0, working: 1, done: 2 };

export const AGENT_STRIP_UNTITLED = "Untitled session";

/** NaN-safe: a missing or malformed stamp sorts last, never poisoning the order. */
const finishedMs = (dot: AgentStripDot): number => {
  const ms = dot.finishedAt ? Date.parse(dot.finishedAt) : Number.NaN;
  return Number.isNaN(ms) ? 0 : ms;
};

export function projectAgentStrip(
  threads: ReadonlyArray<RootSessionStripThreadInput>,
  scope: WorldThreadScope,
  maxDots: number = AGENT_STRIP_MAX_DOTS,
  /** World sessions the World says are retired (`retiredWorldSessionKey`). */
  retired: ReadonlySet<string> = new Set(),
): AgentStripProjection {
  const live: AgentStripDot[] = [];
  for (const thread of threads) {
    const status = thread.status ?? null;
    if (status === null) continue;
    // Archived and snoozed work is out of sight by the person's own choice;
    // a settled session's old result or failure is filed, not news
    // (W-ea4c5090: settled failures stayed as alerts for weeks).
    if (thread.archivedAt !== null || thread.snoozedUntil !== null) continue;
    if (thread.settledAt !== null && (status === "done" || status === "failed")) continue;
    // A World session is archived by retiring it in the World, which leaves
    // this Desk's thread unarchived (W-ea4c5090).
    if (
      thread.worldKey !== null &&
      thread.sessionId != null &&
      retired.has(retiredWorldSessionKey(thread.worldKey, thread.sessionId))
    )
      continue;
    if (!threadBelongsToWorld(thread.worldKey, scope)) continue;
    const label = thread.title?.trim() || AGENT_STRIP_UNTITLED;
    live.push({
      key: `${thread.environmentId}:${thread.threadId}`,
      environmentId: thread.environmentId,
      threadId: thread.threadId,
      worldKey: thread.worldKey,
      sessionId: thread.sessionId ?? null,
      label,
      status,
      state: agentStripState(status),
      description: `${label}, ${ROOT_SESSION_STATUS_LABELS[status]}`,
      finishedAt: thread.finishedAt ?? null,
    });
  }
  // Needs you first, then working, then finished; ties keep a stable order by
  // key, so a dot does not jump while its session's state holds.
  live.sort(
    (left, right) =>
      STATE_ORDER[left.state] - STATE_ORDER[right.state] || left.key.localeCompare(right.key),
  );
  const shown = live.slice(0, Math.max(0, maxDots));
  const finished = live
    .filter((dot) => dot.status === "done" || dot.status === "failed")
    .sort((left, right) => finishedMs(right) - finishedMs(left));
  return {
    sessions: live,
    finished,
    dots: shown,
    overflow: live.length - shown.length,
    // W-fa2578b8 (Can 36): a conversation kept in the World that waits on the person waits on a card, which the strip
    // already counts as a decision ("1 to decide"); counted again here it read "2 to decide, 2 need you" for one decision.
    needsCount: live.filter((dot) => dot.state === "needs" && !decidedElsewhere(dot)).length,
    doneCount: live.filter((dot) => dot.state === "done").length,
  };
}
